<template>
    <div class="as-page">
        <div class="as-header">
            <div class="as-header-left">
                <div class="as-icon-wrap"><i class="fas fa-clipboard-check"></i></div>
                <h2 class="as-title">Attendance Statuses</h2>
                <span class="as-count" v-if="items.length">{{ items.length }}</span>
            </div>
            <AllowPermission :permission="PERMISSION_CODES.EDIT_ATTENDANCE_STATUSES">
                <button class="as-add-btn" @click="showForm = true">
                    <i class="fas fa-plus"></i>
                    Add Status
                </button>
            </AllowPermission>
        </div>

        <RightSideModal :show="showForm" :title="editMode ? 'Edit Status' : 'New Status'" @close="closeForm">
            <Form :fields="createFields" title="Attendance Status" submitUrl="/attendance-statuses"
                updateRootUrl="/attendance-statuses" @success="handleFormSuccess" @cancel="closeForm"
                :editMode="editMode" :editData="editData" />
        </RightSideModal>

        <div v-if="loading" class="as-loading"><div class="as-spinner"></div><span>Loading...</span></div>
        <div v-else-if="!items.length" class="as-empty">
            <div class="as-empty-icon"><i class="fas fa-clipboard-check"></i></div>
            <h3>No attendance statuses found</h3>
        </div>
        <div v-else class="as-grid">
            <div class="as-card" v-for="(item, idx) in items" :key="item.id" :style="{ animationDelay: `${idx * 0.03}s` }">
                <div class="as-card-top">
                    <div class="as-card-icon" :class="asColor(idx)"><i class="fas fa-tag"></i></div>
                    <div class="as-card-info">
                        <h4 class="as-card-name">{{ item.name }}</h4>
                    </div>
                    <span class="as-code-badge">{{ item.code }}</span>
                </div>
                <div class="as-card-actions">
                    <AllowPermission :permission="PERMISSION_CODES.EDIT_ATTENDANCE_STATUSES">
                        <button class="as-action-btn as-btn-edit" @click="enableEditing(item)"><i class="fas fa-pen"></i> Edit</button>
                    </AllowPermission>
                    <AllowPermission :permission="PERMISSION_CODES.EDIT_ATTENDANCE_STATUSES">
                        <button class="as-action-btn as-btn-delete" @click="deleteItem(item.id)"><i class="fas fa-trash-alt"></i> Delete</button>
                    </AllowPermission>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import Form from "../../components/crud/form.vue"
import RightSideModal from "../../components/layout/RightSideModal.vue"
import AllowPermission from "../../components/layout/AllowPermission.vue"
import { createField } from "../../utils/common"
import { PERMISSION_CODES } from "../../store/permission"
import { http } from "../../utils/http"

export default {
    data() {
        return {
            PERMISSION_CODES,
            createFields: [
                createField("Name*", "name", "text", true),
                createField("Code*", "code", "text", true),
            ],
            items: [],
            loading: true,
            showForm: false,
            editMode: false,
            editData: null,
        }
    },
    components: { Form, RightSideModal, AllowPermission },
    mounted() { this.fetchData() },
    methods: {
        asColor(i) { return ["as-clr-blue","as-clr-purple","as-clr-teal","as-clr-amber","as-clr-rose"][i % 5] },
        closeForm() { this.showForm = false; this.editMode = false; this.editData = null },
        handleFormSuccess() { this.closeForm(); this.fetchData() },
        enableEditing(item) { this.editMode = true; this.editData = { ...item }; this.showForm = true; window.scrollTo({ top: 0, behavior: "smooth" }) },
        async fetchData() {
            this.loading = true
            try { const res = await http.get("/attendance-statuses"); this.items = res.data.result || [] } catch (err) { console.log(err) }
            this.loading = false
        },
        async deleteItem(id) {
            if (!confirm("Delete this status?")) return
            try { await http.delete(`/attendance-statuses/${id}`); this.$toast.success("Deleted"); this.items = this.items.filter(c => c.id !== id) }
            catch (e) { this.$toast.error("Error deleting") }
        },
    },
}
</script>

<style lang="scss" scoped>
@import "../../assets/variables.scss";
.as-page { animation: asFade .3s ease; }
@keyframes asFade { from { opacity:0; transform:translateY(4px); } to { opacity:1; transform:translateY(0); } }
.as-header { display:flex; align-items:center; justify-content:space-between; gap:12px; background:white; border:1px solid $gray100; border-radius:8px; padding:10px 16px; margin-bottom:12px; box-shadow:$shadowSm; flex-wrap:wrap; }
.as-header-left { display:flex; align-items:center; gap:10px; }
.as-icon-wrap { width:32px; height:32px; border-radius:8px; background:linear-gradient(135deg,$primaryLight,darken($primaryLight,4%)); color:$primaryColor; display:flex; align-items:center; justify-content:center; font-size:14px; }
.as-title { font-size:15px; font-weight:700; color:$gray800; margin:0; }
.as-count { min-width:20px; height:20px; padding:0 6px; border-radius:$radiusFull; font-size:11px; font-weight:700; background:$primaryColor; color:white; display:inline-flex; align-items:center; justify-content:center; }
.as-add-btn { display:inline-flex; align-items:center; gap:6px; padding:7px 14px; border:none; border-radius:6px; background:$primaryColor; color:white; font-size:12.5px; font-weight:600; cursor:pointer; transition:all $transitionFast; font-family:inherit; &:hover { background:$primaryDark; } }
.as-loading { display:flex; align-items:center; justify-content:center; gap:10px; padding:64px 16px; background:white; border:1px solid $gray100; border-radius:8px; box-shadow:$shadowSm; span { font-size:13px; color:$gray400; } }
.as-spinner { width:24px; height:24px; border:2.5px solid $gray200; border-top-color:$primaryColor; border-radius:50%; animation:asSpin .7s linear infinite; }
@keyframes asSpin { to { transform:rotate(360deg); } }
.as-empty { text-align:center; padding:64px 16px; background:white; border:1px solid $gray100; border-radius:8px; box-shadow:$shadowSm; h3 { font-size:14px; font-weight:700; color:$gray600; margin:0; } }
.as-empty-icon { width:48px; height:48px; border-radius:$radiusFull; background:$gray50; border:1px solid $gray100; display:flex; align-items:center; justify-content:center; margin:0 auto 12px; i { font-size:20px; color:$gray300; } }
.as-grid { display:grid; grid-template-columns:repeat(3,1fr); gap:10px; }
.as-card { background:white; border:1px solid $gray100; border-radius:10px; padding:14px; box-shadow:$shadowSm; display:flex; flex-direction:column; gap:10px; animation:asSlide .25s ease both; transition:all $transitionFast; &:hover { border-color:$gray200; box-shadow:$shadowMd; transform:translateY(-1px); } }
@keyframes asSlide { from { opacity:0; transform:translateY(4px); } to { opacity:1; transform:translateY(0); } }
.as-card-top { display:flex; align-items:center; gap:10px; }
.as-card-icon { width:36px; height:36px; border-radius:8px; display:flex; align-items:center; justify-content:center; font-size:14px; flex-shrink:0; }
.as-clr-blue { background:#eff6ff; color:#2563eb; }
.as-clr-purple { background:#f5f3ff; color:#7c3aed; }
.as-clr-teal { background:#f0fdfa; color:#0d9488; }
.as-clr-amber { background:#fffbeb; color:#d97706; }
.as-clr-rose { background:#fff1f2; color:#e11d48; }
.as-card-info { flex:1; min-width:0; }
.as-card-name { font-size:13.5px; font-weight:700; color:$gray800; margin:0; }
.as-code-badge { font-size:11px; font-weight:700; padding:3px 10px; border-radius:$radiusFull; background:$gray50; color:$gray600; border:1px solid $gray200; letter-spacing:0.5px; flex-shrink:0; }
.as-card-actions { display:flex; gap:6px; margin-top:auto; padding-top:6px; border-top:1px solid $gray100; }
.as-action-btn { display:inline-flex; align-items:center; gap:4px; padding:4px 10px; border:1px solid $gray200; border-radius:5px; font-size:11px; font-weight:600; cursor:pointer; transition:all $transitionFast; font-family:inherit; background:white; i { font-size:10px; } }
.as-btn-edit { color:$primaryColor; &:hover { background:$primaryLight; border-color:rgba(0,94,137,0.15); } }
.as-btn-delete { color:$danger; &:hover { background:#fef2f2; border-color:rgba(220,38,38,0.15); } }
@media(max-width:992px) { .as-grid { grid-template-columns:repeat(2,1fr); } }
@media(max-width:480px) { .as-grid { grid-template-columns:1fr; } }
</style>
